import { useEffect, useState } from "react";

import { ExerciseBadgeModal } from "components";
import { WeeklyChallengeModal } from "components/Modal/WeekChallenge/WeekChallenge";

import type { IBadgeResult } from "pages/Statistics/Tabs/Badges";

import { useCheckAchievementsQuery, useGetWeeklyChallengesQuery, useGetTutoringListQuery } from "api/generated";

import { useContentAccess } from "utils/hooks/useContentAccess";
import { newBadgeDataFormatter } from "utils/newBadgeDataFormatter";
import { processApiError } from "utils/processApiError";

export const ExerciseModals = () => {
  const { data } = useCheckAchievementsQuery();
  const { tutoringVisible } = useContentAccess();

  const [shownBadgeIndex, setShownBadge] = useState(0);
  const [shownChallengeIndex, setShownChallengeIndex] = useState(0);
  const [gainedBadges, setGainedBadges] = useState<IBadgeResult[]>([]);
  const [step, setStep] = useState(0);

  const {
    data: weeklyChallenges,
    error: weeklyChallengesApiError,
    isLoading: isLoadingChallenges,
  } = useGetWeeklyChallengesQuery();
  const { data: tutoringList, error: tutoringListApiError, isLoading: isLoadingTutoring } = useGetTutoringListQuery();

  useEffect(() => {
    if (weeklyChallengesApiError === undefined) return;

    processApiError(weeklyChallengesApiError);
  }, [weeklyChallengesApiError]);

  useEffect(() => {
    if (tutoringListApiError === undefined) return;

    processApiError(tutoringListApiError);
  }, [tutoringListApiError]);

  useEffect(() => {
    if (data === undefined) return;

    const badges = newBadgeDataFormatter(data.checkAchievements);
    setGainedBadges(badges);

    if (!badges.length) setStep(1);
  }, [data]);

  const goNext = () => {
    setShownBadge(index => (shownBadgeIndex + 1 >= gainedBadges.length ? 0 : index + 1));
  };

  const goPrev = () => {
    setShownBadge(index => (shownBadgeIndex <= 0 ? gainedBadges.length - 1 : index - 1));
  };

  const onClose = () => {
    setGainedBadges([]);
    setStep(1);
  };

  const showNextChallenge = () => {
    const nextIndex = shownChallengeIndex + 1;
    if (weeklyChallenges?.weeklyChallenges.at(nextIndex)) {
      setShownChallengeIndex(nextIndex);
    } else {
      setStep(2);
    }
  };

  if (step === 0 && data !== undefined && gainedBadges.length) {
    return (
      <ExerciseBadgeModal
        badge={gainedBadges[shownBadgeIndex]}
        isNavShown={gainedBadges.length > 1}
        isShown={true}
        goNext={goNext}
        goPrev={goPrev}
        onClose={onClose}
      />
    );
  }

  if (step === 1 && !isLoadingChallenges && !isLoadingTutoring) {
    // AIDEV-NOTE: Maturant doučování nemá — bez týhle podmínky by uviděl pozvánku na
    // doučko, klikl a guard (TutoringGuard.tsx) ho vrátí zpátky na nástěnku. Chová se
    // to stejně jako chybějící `challenge`/`tutoringId` níž — přeskočit na další výzvu. [TD-351]
    if (!tutoringVisible) {
      showNextChallenge();
      return null;
    }

    const challenge = weeklyChallenges?.weeklyChallenges.at(shownChallengeIndex);
    if (!challenge) {
      showNextChallenge();
      return null;
    }

    const tutoringId = tutoringList?.userTutoringAll.find(tutoring => tutoring.theme === challenge.theme)?.id;
    if (!tutoringId) {
      showNextChallenge();
      return null;
    }

    return (
      <WeeklyChallengeModal
        themeId={challenge.theme}
        tutoringId={tutoringId}
        onClose={() => {
          showNextChallenge();
        }}
      />
    );
  }

  return null;
};
